import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {timeout} from 'rxjs/operators';
import {ModuleSchemaService} from './main/modules/services/moduleSchema.service';
import {AuthService} from './auth/services/auth.service';
import {ModuleSchemaStoreService} from './services/module-schema-store.service';
import {SchemaBootstrapService} from './services/schema-bootstrap.service';
import {validateModuleSchemaList} from './shared/schema/schema-runtime-validation';

/** Max wait for /auth/profile during bootstrap (AC-21). */
export const AUTH_VALIDATE_TIMEOUT_MS = 8000;

@Injectable()
export class StartupService {

  constructor(
    private listService: ModuleSchemaService,
    private auth: AuthService,
    private moduleSchemaStoreService: ModuleSchemaStoreService,
    private router: Router,
    private schemaBootstrap: SchemaBootstrapService,
  ) {
    this.setSubDomain();
  }

  setSubDomain() {
    const domain = window.location.hostname;
    this.auth.setDomain(domain.substring(0, domain.indexOf('.')));
    if (domain.indexOf('.') < 0 ||
      domain.split('.')[0] === 'example' || domain.split('.')[0] === 'localhost') {
      // this.auth.setBaseUrl('https://test.aysko.com');
      this.auth.setBaseUrl('http://localhost:3000');
    } else {
      const baseUrl = `${window.location.protocol}//${domain}`;
      this.auth.setBaseUrl(baseUrl);
    }
  }

  /**
   * APP_INITIALIZER entry: validate persisted session (if any), then load schemas.
   * Failures are recorded on SchemaBootstrapService — never silently swallowed (AC-35).
   */
  loadModulesSchema(): Promise<void> {
    return this.validateAuthSession().then((valid) => {
      if (!valid) {
        return;
      }
      return this.fetchModulesSchema();
    });
  }

  /** User-initiated retry from the bootstrap failure UI. */
  retryLoadSchemas(): Promise<void> {
    if (!this.schemaBootstrap.beginRetry()) {
      return Promise.resolve();
    }
    return this.fetchModulesSchema(true);
  }

  /**
   * If a local token exists, call GET /auth/profile.
   * @returns false when session was cleared (caller should not load authenticated data).
   */
  validateAuthSession(): Promise<boolean> {
    if (!this.auth.getToken()) {
      this.schemaBootstrap.markNoToken();
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      this.auth.getProfile()
        .pipe(timeout(AUTH_VALIDATE_TIMEOUT_MS))
        .subscribe({
          next: () => resolve(true),
          error: (err) => {
            this.schemaBootstrap.markUnauthorized(err);
            this.failAuthValidation();
            resolve(false);
          },
        });
    });
  }

  private failAuthValidation(): void {
    this.auth.handleUnauthorized();
  }

  private fetchModulesSchema(isRetry = false): Promise<void> {
    return new Promise((resolve) => {
      const token = this.auth.getToken();
      if (!token) {
        this.moduleSchemaStoreService.clear();
        this.schemaBootstrap.markNoToken();
        resolve();
        return;
      }
      if (!isRetry && !this.schemaBootstrap.beginLoad()) {
        resolve();
        return;
      }
      this.moduleSchemaStoreService.markLoading();
      this.listService.getAllTablesSchema()
        .subscribe({
          next: (d) => {
            const validationError = this.validateModuleSchemaList(d);
            if (validationError) {
              this.moduleSchemaStoreService.markError(validationError);
              this.schemaBootstrap.markMalformed(validationError);
              resolve();
              return;
            }
            this.moduleSchemaStoreService.set(d);
            this.schemaBootstrap.markLoaded();
            resolve();
          },
          error: (error) => {
            console.error('Error loading module schemas:', error);
            this.moduleSchemaStoreService.markError(error);
            const status = error?.status;
            if (status === 401 || status === 403) {
              this.schemaBootstrap.markUnauthorized(error);
              this.auth.handleUnauthorized();
            } else {
              this.schemaBootstrap.markUnavailable(error);
            }
            resolve();
          },
        });
    });
  }

  /** Lightweight structural check before full form validation (AC-34). */
  private validateModuleSchemaList(data: unknown): Error | null {
    const result = validateModuleSchemaList(data);
    if (result.ok) {
      return null;
    }
    return result.errors[0] ?? new Error('Invalid module schemas');
  }
}
